import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/common/Layout';
import LoginPage from '@/pages/LoginPage';
import PasswordLoginPage from '@/pages/PasswordLoginPage';
import RegisterPage from '@/pages/RegisterPage';
import GitHubCallbackPage from '@/pages/GitHubCallbackPage';
import WelcomePage from '@/pages/WelcomePage';
import DashboardPage from '@/pages/DashboardPage';
import TeamsPage from '@/pages/TeamsPage';
import TeamHubPage from '@/pages/TeamHubPage';
import TeamOnboardingPage from '@/pages/TeamOnboardingPage';
import TeamOverviewTab from '@/pages/team-tabs/TeamOverviewTab';
import TeamKnowledgeTab from '@/pages/team-tabs/TeamKnowledgeTab';
import TeamChatbotTab from '@/pages/team-tabs/TeamChatbotTab';
import TeamSettingsTab from '@/pages/team-tabs/TeamSettingsTab';
import TeamAgentTab from '@/pages/team-tabs/TeamAgentTab';
import AdminHubPage from '@/pages/AdminHubPage';
import AdminCataloguePage from '@/pages/AdminCataloguePage';
import AgentLessonsPanel from '@/pages/AgentLessonsPanel';

import AdminTeamsPage from '@/pages/AdminTeamsPage';
import AdminIntegrationsPage from '@/pages/AdminIntegrationsPage';
import UserManagementPage from '@/pages/UserManagementPage';
import AdminFeedbackPage from '@/pages/AdminFeedbackPage';
import NewsletterTopicsPage from '@/pages/NewsletterTopicsPage';
import LogsPage from '@/pages/LogsPage';
import DocumentationPage from '@/pages/DocumentationPage';
import FeedbackPage from '@/pages/FeedbackPage';
import ChangelogPage from '@/pages/ChangelogPage';
import SessionConsolePage from '@/pages/SessionConsolePage';
import TaskConsolePage from '@/pages/TaskConsolePage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/sessions/:sessionId/console" element={<SessionConsolePage />} />
      <Route path="/tasks/:taskId/console" element={<TaskConsolePage />} />

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/password" element={<PasswordLoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/github/callback" element={<GitHubCallbackPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="teams" element={<TeamsPage />} />

        {/* Team Hub with nested routes */}
        <Route path="teams/:teamId" element={<TeamHubPage />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<TeamOverviewTab />} />
          <Route path="knowledge" element={<TeamKnowledgeTab />} />
          <Route path="chatbot" element={<TeamChatbotTab />} />
          <Route path="settings" element={<TeamSettingsTab />} />
          <Route path="agent" element={<TeamAgentTab />} />
          {/* Legacy redirects for tabs now inside Chatbot */}
          <Route path="ai" element={<Navigate to="../chatbot" replace />} />
          <Route path="analytics" element={<Navigate to="../chatbot" replace />} />
          <Route path="interactions" element={<Navigate to="../chatbot" replace />} />
          <Route path="issues" element={<Navigate to="../chatbot" replace />} />
        </Route>

        {/* Onboarding is a separate wizard, not nested in hub */}
        <Route path="teams/:teamId/onboarding" element={<TeamOnboardingPage />} />

        {/* Legacy redirects for old routes */}
        <Route path="teams/:teamId/prompting" element={<Navigate to="../ai" replace />} />
        <Route path="teams/:teamId/github-issues" element={<Navigate to="../issues" replace />} />

        {/* Global pages */}
        <Route path="docs" element={<DocumentationPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="newsletter" element={<AdminRoute><NewsletterTopicsPage /></AdminRoute>} />
        <Route path="changelog" element={<ChangelogPage />} />

        {/* Legacy route redirects */}
        <Route path="analytics" element={<Navigate to="/teams" replace />} />
        <Route path="interactions" element={<Navigate to="/teams" replace />} />
        <Route path="cache" element={<Navigate to="/teams" replace />} />

        {/* Admin routes */}
        <Route path="admin/approvals" element={<Navigate to="/admin/teams" replace />} />
        <Route path="admin" element={<AdminRoute><AdminHubPage /></AdminRoute>}>
          <Route index element={<Navigate to="teams" replace />} />
          <Route path="teams" element={<AdminTeamsPage />} />
          <Route path="users" element={<UserManagementPage />} />
          <Route path="feedback" element={<AdminFeedbackPage />} />
          <Route path="newsletter" element={<Navigate to="/newsletter" replace />} />
          <Route path="integrations" element={<AdminIntegrationsPage />} />
          <Route path="catalog" element={<AdminCataloguePage />} />
          <Route path="lessons" element={<AgentLessonsPanel />} />
          <Route path="logs" element={<LogsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
